import {
  organizationInvitationAcceptRequestSchema,
  organizationInvitationAcceptResponseSchema
} from '@inktap/contracts/workspace-organization';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { request } from '../lib/api';
import { needsKey, needsTotp, type CustomerLoginResult } from '../lib/authSecurity';
import { TotpLoginPrompt } from '../components/TotpLoginPrompt';
import { KeyLoginPrompt } from '../components/KeyLoginPrompt';
import { toSession } from '../lib/userSession';
import { useSession } from '../session';

function OrganizationInvitationPage() {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token'), [searchParams]);
  const { setSession } = useSession();
  const navigate = useNavigate();
  const [rememberMe, setRememberMe] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [keyChallenge, setKeyChallenge] = useState<{ magicToken: string; hardwareRequired: boolean } | null>(null);
  const [totpToken, setTotpToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const acceptInvitation = async () => {
    if (!token) {
      setError('This organization invitation link is incomplete. Ask the sender for a new invitation.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const payload = organizationInvitationAcceptRequestSchema.parse({ token, rememberMe });
      const accepted = organizationInvitationAcceptResponseSchema.parse(await request<unknown>('/organizations/invitations/accept', {
        method: 'POST',
        body: JSON.stringify(payload)
      }));
      const response = await request<CustomerLoginResult>('/auth/verify-magic', {
        method: 'POST',
        body: JSON.stringify({ token: accepted.magicToken })
      });
      if (needsKey(response)) { setKeyChallenge(response); return; }
      if (needsTotp(response)) { setTotpToken(response.magicToken); return; }
      setSession(toSession(response));
      navigate('/dashboard', { replace: true });
    } catch {
      setError('Unable to accept this organization invitation. Ask the sender for a new one.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-shell organization-invitation">
      <div className="card-panel auth-shell__card">
        <p className="portal-eyebrow">Organization invitation</p>
        <h1>Join your InkTap team</h1>
        <p className="helper-text">
          Accepting creates or connects your own InkTap sign-in. Your organization never receives your personal login
          credentials.
        </p>
        {error || !token ? (
          <div className="alert" role="alert">
            {error ?? 'This organization invitation link is incomplete. Ask the sender for a new invitation.'}
          </div>
        ) : null}
        {totpToken ? <TotpLoginPrompt magicToken={totpToken} onSuccess={response => { setSession(toSession(response)); navigate('/dashboard', { replace: true }); }} /> : keyChallenge ? <KeyLoginPrompt magicToken={keyChallenge.magicToken} hardwareRequired={keyChallenge.hardwareRequired} onSuccess={response => { setSession(toSession(response)); navigate('/dashboard', { replace: true }); }} /> : <>
        <label className="auth-remember">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(event) => setRememberMe(event.target.checked)}
            disabled={submitting}
          />
          <span>
            <strong>Keep me signed in for 30 days</strong>
            <small>Leave this off on a shared device. Otherwise, this session lasts 12 hours.</small>
          </span>
        </label>
        <button className="button-primary" type="button" onClick={acceptInvitation} disabled={submitting || !token}>
          {submitting ? 'Joining organization...' : 'Accept invitation'}
        </button>
        </>}
        <p className="helper-text">
          Not expecting this invitation? You can safely leave this page. <Link to="/login">Go to sign in</Link>
        </p>
      </div>
    </div>
  );
}

export default OrganizationInvitationPage;
